// Renderer + post-processing pipeline.
//
// Owns the WebGL renderer, the EffectComposer (bloom → output), the resize
// handler, AND the single point where graphics-quality settings (settings.ts)
// are applied to the live renderer + sun shadow. `makeRenderer` follows the same
// factory shape as `makeDayNight`: pass the scene + camera + sun, get back the
// render/resize/toggleBloom handles the loop needs. It also publishes live
// diagnostics (`renderInfo`) for the F3 HUD.

import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { SMAAPass } from "three/examples/jsm/postprocessing/SMAAPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { ToonOutlinePass } from "./outline.ts";
import { BLOOM } from "./config.ts";
import { setRimEnabled } from "./toon.ts";
import { onQualityChange, quality, type GraphicsSettings } from "./settings.ts";

export type Renderer = {
  render: () => void;
  toggleBloom: () => void;
  /** Compile every scene material against the CURRENT light set without drawing
   *  (three's renderer.compile). game.ts calls it once per world build behind the
   *  loading/swap fade with the lamps temporarily flipped visible: the visible
   *  point-light COUNT is baked into every program (NUM_POINT_LIGHTS), so the
   *  first dusk otherwise recompiles every toon variant in one hitchy frame. */
  prewarm: () => void;
  /** Swap the camera the composer renders through (plan 20 — the studio world
   *  editor's ortho top-down mode; the ONLY way to run a second camera through
   *  the bloom pipeline). The game never calls this. */
  setCamera: (cam: THREE.Camera) => void;
  /** Override the render-resolution multiplier at runtime (studio world editor's
   *  perf panel). Clamped to native devicePixelRatio — never supersamples beyond
   *  the device. The GAME never calls this: it drives pixel ratio from its quality
   *  preset inside the sun branch above, so exposing this changes nothing the game
   *  executes. */
  setPixelRatio: (ratio: number) => void;
  /** Enable/disable bloom directly — idempotent, unlike toggleBloom (studio world
   *  editor's perf panel; the game uses toggleBloom + its preset). */
  setBloom: (on: boolean) => void;
  /** Enable/disable the toon outline pass (studio perf panel; game = preset). */
  setOutline: (on: boolean) => void;
};

/** Live renderer diagnostics, read by the F3 HUD (imdebug.ts). */
export type RenderInfo = {
  fps: number; // smoothed
  frameMs: number; // smoothed frame time (rAF cadence = true frame time)
  worstMs: number; // worst frame in the last ~1s — the hitch indicator
  calls: number; // draw calls last frame
  triangles: number;
  geometries: number; // resident GPU geometries (leak watch)
  textures: number;
  programs: number;
  pixelRatio: number; // effective
  quality: string;
  gpu: string; // unmasked renderer string — which GPU is this player actually on?
};

let _info: (() => RenderInfo) | null = null;
/** Live renderer diagnostics; null until makeRenderer has run. */
export function renderInfo(): RenderInfo | null {
  return _info?.() ?? null;
}

export function makeRenderer(
  canvas: HTMLCanvasElement,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera,
  // The sun opts the renderer into the game's quality-settings system (pixel
  // ratio / bloom / shadow scaling driven by settings.ts). Omitted by the Model
  // Studio, which always renders at full fixed quality (its own sun is created
  // after this, and manages its own shadow map) — so the studio stays untouched.
  sun?: THREE.DirectionalLight,
): Renderer {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    // MSAA is only live on the DIRECT render path (the composer renders into its
    // own non-multisampled RTs) — kept on because the no-post path below renders
    // straight to the canvas, which is exactly the preset (Low) that needs AA.
    antialias: true,
    stencil: false, // nothing in the pipeline uses the stencil buffer
    powerPreference: "high-performance", // hint the discrete GPU on hybrid laptops
  });
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap; // PCFSoft is deprecated (falls back to PCF with a warning)

  // Post-processing: outline → bloom → SMAA → output (plan 23).
  const composer = new EffectComposer(renderer);
  // Scene depth for the outline pass: both RTs get their OWN DepthTexture (the
  // buffer roles alternate frames — the pass binds readBuffer.depthTexture per
  // frame). Attached LAZILY: the outline pass is the only depth consumer and
  // every preset ships it off, so by default the RTs keep their plain (cheaper,
  // non-sampleable) depth renderbuffer and no depth texture is written at all.
  function setDepthTextures(on: boolean) {
    if (on === !!composer.renderTarget1.depthTexture) return;
    for (const rt of [composer.renderTarget1, composer.renderTarget2]) {
      rt.depthTexture?.dispose();
      // setSize fixes the dims on first bind (WebGLTextures) — 1×1 is fine here.
      rt.depthTexture = on ? new THREE.DepthTexture(1, 1) : (null as unknown as THREE.DepthTexture);
      rt.dispose(); // rebuild the FBO with/without the depth attachment on next bind
    }
  }
  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);
  const outline = new ToonOutlinePass(camera);
  composer.addPass(outline);
  const bloom = new UnrealBloomPass(
    new THREE.Vector2(1, 1), // resized in resize()
    BLOOM.strength, // halo intensity
    BLOOM.radius, // how far the glow spreads
    BLOOM.threshold, // only pixels brighter than this glow (sky, water, sunlit roofs)
  );
  composer.addPass(bloom);
  // SMAA fixes the dead-MSAA defect (board bug B2): antialias:true only covers
  // the default framebuffer, which the composer bypasses — and the outline pass
  // sharpens silhouettes, which amplifies the aliasing. Runs in linear-srgb, so
  // it must sit BEFORE OutputPass (SMAAPass class doc).
  const smaa = new SMAAPass();
  composer.addPass(smaa);
  const output = new OutputPass(); // tone-map + correct color after bloom
  composer.addPass(output);

  // GPU string, read once. The single fastest way to answer "which GPU is this
  // player rendering on?" — an integrated Intel/AMD iGPU vs a discrete card is
  // the usual reason the same scene runs smoothly for one player and not another.
  let gpu = "unknown";
  try {
    const gl = renderer.getContext();
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    if (dbg) gpu = String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL));
  } catch {
    /* extension blocked for privacy — leave "unknown" */
  }

  function resize() {
    const w = window.innerWidth, h = window.innerHeight;
    renderer.setSize(w, h);
    // Keep the composer's pixel ratio in lockstep with the renderer's. Without
    // this the composer keeps the ratio it captured at CONSTRUCTION (1×, before
    // the quality preset applied) and the whole post chain renders at 1× and
    // upscales — the presets' pixelRatio was silently inert (found in plan 23).
    composer.setPixelRatio(renderer.getPixelRatio());
    composer.setSize(w, h); // sizes every pass at effective pixels — no per-pass overrides
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  addEventListener("resize", resize);

  // ---- Quality settings: apply on change, and once immediately on subscribe ----
  let shadowInterval = 1;
  if (sun) {
    onQualityChange((s: GraphicsSettings) => {
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, s.pixelRatio));
      resize(); // re-size the render targets to the new pixel ratio
      bloom.enabled = s.bloom;
      outline.enabled = s.outline;
      setDepthTextures(s.outline);
      smaa.enabled = s.smaa;
      setRimEnabled(s.rim); // Low compiles the rim out of every toon program
      shadowInterval = Math.max(1, s.shadowInterval);
      // Sun shadow: size 0 = off. Dispose the old map so it rebuilds at the new
      // size (or clears when turned off — no frozen shadow lingers). We drive the
      // sun's OWN autoUpdate flag (per-light), never renderer.shadowMap.autoUpdate,
      // which is global and would clobber the lamps' hand-managed shadow bakes.
      if (s.shadowMapSize > 0) {
        sun.castShadow = true;
        sun.shadow.mapSize.set(s.shadowMapSize, s.shadowMapSize);
      } else {
        sun.castShadow = false;
      }
      sun.shadow.map?.dispose();
      sun.shadow.map = null;
      // interval 1 → let three auto-update every frame (byte-identical to before);
      // >1 → we drive needsUpdate ourselves on a cadence in render().
      sun.shadow.autoUpdate = shadowInterval === 1;
    });
  } else {
    // No sun (Model Studio): keep the historical fixed defaults — full quality,
    // independent of the persisted game preset. SMAA on; outline OFF to match
    // the in-world look (the game presets ship the outline off per user pref).
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    bloom.enabled = true;
    outline.enabled = false;
    smaa.enabled = true;
  }
  resize();

  // ---- Frame timing (rAF cadence = true frame time) + 1s hitch window ---------
  let lastTs = performance.now();
  let emaMs = 1000 / 60;
  let worstMs = 0, worstReset = lastTs;
  let shadowTick = 0;

  _info = () => ({
    fps: emaMs > 0 ? 1000 / emaMs : 0,
    frameMs: emaMs,
    worstMs,
    calls: renderer.info.render.calls,
    triangles: renderer.info.render.triangles,
    geometries: renderer.info.memory.geometries,
    textures: renderer.info.memory.textures,
    programs: renderer.info.programs?.length ?? 0,
    pixelRatio: renderer.getPixelRatio(),
    quality: quality(),
    gpu,
  });

  return {
    render: () => {
      // Throttle sun shadow re-renders when the preset asks for it (autoUpdate is
      // off in that case, so nothing updates unless we flag it).
      if (sun && sun.castShadow && shadowInterval > 1) {
        if (shadowTick % shadowInterval === 0) sun.shadow.needsUpdate = true;
        shadowTick++;
      }
      // Outline tint tracks the LIVE sky (daynight + weather grading have both
      // run by now — render is the last call in the frame): fog color swings
      // powder-blue → navy → dusk orange → storm slate, and lines dissolve
      // into whatever the current fog envelope is.
      if (outline.enabled && scene.fog) {
        const fog = scene.fog as THREE.Fog;
        (outline.uniforms.uSky!.value as THREE.Color).copy(fog.color);
        if (fog.isFog) outline.uniforms.uFogFar!.value = fog.far;
      }
      // With every optional pass off (Low ships bloom/outline/smaa all off) the
      // composer is pure overhead: the scene would render into a HalfFloat RT
      // and OutputPass would burn a fullscreen quad doing only the sRGB convert
      // a direct render applies for free (toneMapping is NoToneMapping, output
      // color space the default — pixel-identical either way). Rendering direct
      // also hits the canvas's real framebuffer, so MSAA is live on this path.
      // Derived from the LIVE pass list — only the always-on shell (RenderPass
      // + OutputPass) doesn't count — so a future composer.addPass is respected
      // here automatically instead of being silently skipped on Low.
      let post = false;
      for (const p of composer.passes) {
        if (p.enabled && p !== renderPass && p !== output) { post = true; break; }
      }
      if (post) composer.render();
      else renderer.render(scene, renderPass.camera);
      // Frame timing: measure the gap between successive presents.
      const now = performance.now();
      const dt = now - lastTs;
      lastTs = now;
      emaMs = emaMs * 0.9 + dt * 0.1;
      if (dt > worstMs) worstMs = dt;
      if (now - worstReset > 1000) { worstMs = dt; worstReset = now; } // reset the 1s window
    },
    toggleBloom: () => (bloom.enabled = !bloom.enabled),
    prewarm: () => {
      renderer.compile(scene, camera);
    },
    setCamera: (cam: THREE.Camera) => {
      renderPass.camera = cam;
      // The outline pass linearizes depth with the ACTIVE camera's near/far and
      // projection kind (the studio's top-down swap is an ortho cam, far 3000).
      outline.camera = cam;
    },
    setPixelRatio: (ratio: number) => {
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, ratio));
      resize(); // re-size the render targets + bloom to the new ratio
    },
    setBloom: (on: boolean) => {
      bloom.enabled = on;
    },
    setOutline: (on: boolean) => {
      outline.enabled = on;
      setDepthTextures(on);
    },
  };
}
